<script>
  // User Page (docs/superpowers/specs/2026-09-23-user-page-prototype.md).
  // One person at a glance: who they are, where you know them from (shared
  // groups), what they hold that you want (binder corner badges), and what you
  // hold that they want (the Want list chip). Visible only to the owner and
  // their Network; everyone else gets the same "not available" as an unknown
  // handle, and anonymous viewers (who have no Network) are asked to sign in.
  // The server shapes the payload for this viewer (binders sorted by hits,
  // viewer-relative data empty on your own page), so the template only checks
  // emptiness.

  import { navigate } from '../../lib/stores/router.svelte.js';
  import { authState, isUserAnonymous } from '../../lib/stores/auth.svelte.js';
  import { fetchUserProfile } from '../../lib/services/wantlist-api.js';
  import {
    binderTotals,
    buildUserPageUrl,
    countLabel,
    wantHitsTitle,
  } from '../../lib/logic/user-page-logic.js';
  import Avatar from '../shared/Avatar.svelte';
  import GroupChip from '../shared/GroupChip.svelte';
  import BinderCard from '../home/BinderCard.svelte';
  import SignInPrompt from '../shared/SignInPrompt.svelte';

  /** @typedef {import('../../lib/types.js').UserProfile} UserProfile */

  let { handle = 'me' } = $props();

  let loading = $state(true);
  // null after loading means not found (or not visible to you).
  let profile = $state(/** @type {UserProfile | null} */ (null));

  const totals = $derived(binderTotals(profile?.binders ?? []));

  $effect(() => {
    if (authState.ready && !isUserAnonymous()) load(handle);
  });

  /** @param {string} h */
  async function load(h) {
    loading = true;
    const res = await fetchUserProfile(h);
    if (h !== handle) return; // a newer handle was requested meanwhile
    profile = res.ok && res.data ? res.data : null;
    loading = false;
    // /u/@me is a convenience; show the real, shareable handle once known.
    if (profile?.isSelf && profile.handle && h !== profile.handle) {
      history.replaceState(history.state, '', buildUserPageUrl(profile.handle));
    }
  }
</script>

<main class="user-page">
  {#if authState.ready && isUserAnonymous()}
    <SignInPrompt
      title="Sign in to see profiles"
      text="Profiles are visible to people who share a group."
    />
  {:else if loading}
    <div class="user-loading">Loading profile…</div>
  {:else if !profile}
    <!-- Unknown handle and outside your Network look identical: no existence leak. -->
    <div class="user-empty">
      <h2>Profile not available</h2>
      <p>You can see the profiles of people who share a group with you.</p>
    </div>
  {:else}
    <header class="profile-header">
      <div class="profile-avatar">
        <Avatar id={profile.uid} name={profile.displayName} photoUrl={profile.photoURL} />
      </div>

      <div class="profile-identity">
        <h1>{profile.displayName}</h1>
        <div class="profile-handle">@{profile.handle}</div>
        <div class="profile-stats">
          <span>{countLabel(totals.binders, 'binder')}</span>
          <span aria-hidden="true">·</span>
          <span>{countLabel(totals.cards, 'card')}</span>
        </div>
      </div>

      <button
        class="want-list-btn"
        onclick={() => navigate('wantlist', { handle: profile?.handle })}
      >
        <span class="want-list-label">{profile.isSelf ? 'My want list' : 'Want list'}</span>
        <span class="want-list-sub">{countLabel(profile.wantListSize, 'card')}</span>
        {#if profile.youHaveTheyWant > 0}
          <span class="corner-count have" title="Cards on their want list that you hold">
            You have {profile.youHaveTheyWant}
          </span>
        {/if}
      </button>
    </header>

    {#if profile.sharedGroups.length > 0}
      <section class="profile-section">
        <h2>Shared groups</h2>
        <ul class="group-chips">
          {#each profile.sharedGroups as group (group.groupId)}
            <li>
              <GroupChip
                {group}
                count={group.memberCount}
                onclick={() => navigate('group-detail', { groupId: group.groupId })}
              />
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <section class="profile-section">
      <div class="section-head">
        <h2>Binders</h2>
        {#if profile.theyHaveYouWant > 0}
          <span class="section-note">
            <span class="hit-dot" aria-hidden="true"></span>
            {wantHitsTitle(profile.theyHaveYouWant)}
          </span>
        {/if}
      </div>
      {#if profile.binders.length === 0}
        <p class="section-empty">No binders shared yet.</p>
      {:else}
        <ul class="binder-list">
          <!-- One card per Claim; an unsynced Claim (no binder id yet) keys on its URL. -->
          {#each profile.binders as binder, i (binder.binderId ?? binder.url)}
            <BinderCard {binder} index={i} showMenu={false}>
              {#snippet badge()}
                {#if binder.wantHits}
                  <span
                    class="corner-count want"
                    title={wantHitsTitle(binder.wantHits)}
                    aria-label={wantHitsTitle(binder.wantHits)}
                  >
                    {binder.wantHits}
                  </span>
                {/if}
              {/snippet}
            </BinderCard>
          {/each}
        </ul>
      {/if}
    </section>
  {/if}
</main>

<style>
  /* 72px on top clears the sidebar's fixed menu button (16px + 44px), which
     sits over the page's top-left corner whenever the content reaches it. */
  .user-page {
    padding: 72px var(--space-xl) 80px;
    max-width: 960px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
  }

  .user-loading,
  .user-empty {
    padding: 64px 0;
    text-align: center;
    color: var(--color-text-tertiary);
  }

  .user-empty h2 {
    font-family: var(--font-serif);
    color: var(--color-text);
    margin: 0 0 8px;
  }

  /* ---- Header ---- */

  .profile-header {
    display: flex;
    align-items: center;
    gap: var(--space-lg);
    padding: var(--space-lg);
    background: var(--color-bg-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
  }

  .profile-avatar {
    flex-shrink: 0;
    display: flex;
    --avatar-size: 88px;
    --avatar-font-size: 38px;
    --avatar-font-family: var(--font-serif);
    --avatar-shadow: 0 0 0 3px var(--color-bg-surface), 0 0 0 4px var(--color-border);
  }

  .profile-identity {
    flex: 1;
    min-width: 0;
  }

  .profile-identity h1 {
    font-family: var(--font-serif);
    font-size: 28px;
    font-weight: 700;
    line-height: 1.15;
    color: var(--color-text);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .profile-handle {
    font-size: 14px;
    color: var(--color-text-tertiary);
    margin-top: 2px;
  }

  .profile-stats {
    display: flex;
    gap: 6px;
    margin-top: var(--space-sm);
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  /* Two-line button whose chip sits on its corner, the same place the binder
     badges sit, so "counts on corners" reads as one idea across the page. */
  .want-list-btn {
    position: relative;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 12px 18px;
    background: var(--color-accent-dark);
    color: var(--color-bg);
    border: none;
    border-radius: var(--radius-md);
    font-family: inherit;
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-standard);
  }

  .want-list-btn:hover {
    background: var(--color-accent-hover);
  }

  .want-list-label {
    font-size: 15px;
    font-weight: 600;
  }

  .want-list-sub {
    font-size: 12px;
    opacity: 0.7;
  }

  /* Counts that hang off a corner: the Want list chip and each binder's badge.
     Green = cards you can give them; purple = cards you want (the same purple
     as BinderCard's own want badge). */
  .corner-count {
    position: absolute;
    top: -10px;
    right: -10px;
    min-width: 24px;
    height: 24px;
    padding: 0 8px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    border-radius: 999px;
    border: 2px solid var(--color-bg);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    pointer-events: none;
    z-index: 2;
  }

  .corner-count.have {
    background: var(--color-own-supply);
  }

  .corner-count.want {
    font-size: 12px;
    background: linear-gradient(135deg, #7c3aed, #9333ea);
  }

  /* ---- Sections ---- */

  .profile-section {
    margin-top: var(--space-xl);
  }

  .section-head {
    display: flex;
    align-items: baseline;
    gap: var(--space-md);
    flex-wrap: wrap;
  }

  .profile-section h2 {
    font-family: var(--font-serif);
    font-size: 19px;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 var(--space-md);
  }

  .section-note {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  .hit-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7c3aed, #9333ea);
  }

  .section-empty {
    font-size: 14px;
    color: var(--color-text-tertiary);
    margin: 0;
  }

  .group-chips {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-sm);
  }

  .binder-list {
    list-style: none;
    /* Room for the corner badges, which hang outside each binder. */
    padding: 10px 10px 0 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
  }

  @media (max-width: 640px) {
    .user-page {
      padding: 72px var(--space-md) 80px;
    }

    .profile-header {
      flex-wrap: wrap;
      gap: var(--space-md);
      padding: var(--space-md);
    }

    .profile-avatar {
      --avatar-size: 64px;
      --avatar-font-size: 28px;
    }

    .profile-identity h1 {
      font-size: 22px;
    }

    .want-list-btn {
      width: 100%;
    }

    .binder-list {
      justify-content: center;
    }
  }
</style>
